import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMarket } from '../../context/MarketContext';

/**
 * Floating region-detection notification — shown when IP-detected market differs
 * from the currently active market. Never shown for ad traffic.
 *
 * Rendered as a fixed bottom card so it doesn't compete with the AnnouncementBar.
 * When the user switches, we also navigate to the market-prefixed URL if one exists.
 */
export default function MarketBanner() {
  const { suggestedMarket, setCurrentMarket, dismissSuggestion, currentMarket } = useMarket();
  const navigate = useNavigate();
  const location = useLocation();

  if (!suggestedMarket || suggestedMarket.code === currentMarket?.code) return null;

  const handleSwitch = () => {
    setCurrentMarket(suggestedMarket);

    // If the suggested market uses URL prefixes (e.g. /us), navigate there explicitly.
    // The MarketPrefixGuard alone is not sufficient because it only fires on
    // location.pathname changes, not market context changes triggered from this banner.
    if (suggestedMarket.urlPrefix) {
      const prefix = suggestedMarket.urlPrefix.replace(/^\//, '');
      const currentPath = location.pathname;
      const alreadyPrefixed = currentPath === `/${prefix}` || currentPath.startsWith(`/${prefix}/`);
      if (!alreadyPrefixed) {
        const targetPath = currentPath === '/' ? `/${prefix}` : `/${prefix}${currentPath}`;
        navigate(`${targetPath}${location.search}`, { replace: true });
      }
    }
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-fade-in-up">
      <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.18)] border border-gray-200 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center shrink-0 text-xl">
          🌍
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink leading-snug">
            Looks like you're in <span className="text-primary">{suggestedMarket.name}</span>
          </p>
          <p className="text-xs text-ink-soft mt-0.5">
            Shop in {suggestedMarket.currencySymbol} {suggestedMarket.currencyCode}?
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleSwitch}
            className="bg-primary text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            Switch →
          </button>
          <button
            onClick={dismissSuggestion}
            aria-label="Dismiss"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-muted hover:text-ink hover:bg-gray-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
